import { PLICHI, SOGGETTO_PRINCIPALE, STATI_DOCUMENTO } from "@app/schema";
import type { ChecklistFields, ModelloChecklistFields, Plico, StatoDocumento } from "@app/schema";
import type { FieldsPatch, ListItem } from "@app/sp-data";
import { controllaLunghezze, type Gara, type GareRepos, type Lotto } from "./gare";

/**
 * Checklist dei documenti di gara: una per la gara senza lotti, una per ciascun lotto.
 * Si prepara dal "Modello checklist" (documenti che si chiedono di solito), poi si
 * aggiungono o tolgono voci a mano. Le voci "per componente" si ripetono per ogni
 * componente del raggruppamento.
 */

export type VoceChecklist = ListItem<ChecklistFields>;
export type VoceModello = ListItem<ModelloChecklistFields>;

/* ------------------------------- Componenti ------------------------------- */

/** Altre imprese dell'RTI, dal campo della gara ("A; B" → ["A", "B"]). */
export function mandanti(g: Gara): string[] {
  return (g.fields.Mandanti ?? "")
    .split(/[;\n]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Testo del campo dalle mandanti (senza ripetizioni né il soggetto principale). */
export function testoMandanti(nomi: string[]): string {
  const visti = new Set<string>([SOGGETTO_PRINCIPALE.toLowerCase()]);
  return nomi
    .map((x) => x.trim())
    .filter((x) => x && !visti.has(x.toLowerCase()) && visti.add(x.toLowerCase()))
    .join("; ");
}

/** Orizzonte Altri Colori e, in RTI, le mandanti. */
export const componenti = (g: Gara) => [SOGGETTO_PRINCIPALE, ...mandanti(g)];
export const inRTI = (g: Gara) => mandanti(g).length > 0;

/* ------------------------------- Ordinamento ------------------------------- */

const ordinePlico = (p?: Plico) => (p ? PLICHI.indexOf(p) : PLICHI.length);

export function ordinaVoci<T extends { fields: { Plico?: Plico; Ordine?: number; Soggetto?: string; Title?: string } }>(voci: T[], soggetti: string[] = []): T[] {
  const posSoggetto = (s?: string) => (!s ? -1 : soggetti.indexOf(s) === -1 ? soggetti.length : soggetti.indexOf(s));
  return [...voci].sort(
    (a, b) =>
      ordinePlico(a.fields.Plico) - ordinePlico(b.fields.Plico) ||
      (a.fields.Ordine ?? 9999) - (b.fields.Ordine ?? 9999) ||
      (a.fields.Title ?? "").localeCompare(b.fields.Title ?? "", "it") ||
      posSoggetto(a.fields.Soggetto) - posSoggetto(b.fields.Soggetto),
  );
}

/* ------------------------------- Lettura ------------------------------- */

export async function loadModello(r: GareRepos, soloAttivi = false): Promise<VoceModello[]> {
  const items = await r.modelloChecklist.queryAll({ top: 500 });
  return ordinaVoci(soloAttivi ? items.filter((x) => x.fields.Attivo !== false) : items);
}

/** Tutte le voci della gara (di tutti i lotti). */
export function loadChecklist(r: GareRepos, garaId: number): Promise<VoceChecklist[]> {
  return r.checklist.queryAll({ filter: `fields/GaraLookupId eq ${garaId}`, top: 500, allowNonIndexed: true });
}

/** Voci di una checklist: della gara senza lotti (lotto undefined) o del lotto. */
export const vociDi = (voci: VoceChecklist[], lotto?: Lotto) => voci.filter((v) => (v.fields.Lotto ?? undefined) === (lotto?.id ?? undefined));

/* ------------------------------- Avanzamento ------------------------------- */

export interface Avanzamento {
  totale: number;
  /** Definitivi o approvati. */
  pronti: number;
  perStato: Record<StatoDocumento, number>;
}

export const PRONTO: readonly StatoDocumento[] = ["definitivo", "approvato"];

export function avanzamento(voci: VoceChecklist[]): Avanzamento {
  const perStato = Object.fromEntries(STATI_DOCUMENTO.map((s) => [s, 0])) as Record<StatoDocumento, number>;
  for (const v of voci) perStato[v.fields.Stato ?? "da fare"]++;
  return { totale: voci.length, pronti: voci.filter((v) => PRONTO.includes(v.fields.Stato ?? "da fare")).length, perStato };
}

/* ------------------------------- Preparazione ------------------------------- */

/** Voce del modello proposta: già selezionata se "proposta" e, per quelle solo RTI, se la gara è in RTI. */
export const propostaDiDefault = (m: VoceModello, rti: boolean) => m.fields.Predefinito !== false && (!m.fields.SoloRTI || rti);

/** Chiave per riconoscere un documento già presente nella checklist. */
const chiave = (titolo?: string, soggetto?: string) => `${(titolo ?? "").trim().toLowerCase()}|${(soggetto ?? "").trim().toLowerCase()}`;

export interface NuovaVoce {
  Title: string;
  Plico: Plico;
  Soggetto?: string;
  Ordine?: number;
}

/** Righe da creare per le voci scelte del modello, saltando quelle già presenti. */
export function righeDaModello(scelte: VoceModello[], soggetti: string[], esistenti: VoceChecklist[] = []): NuovaVoce[] {
  const gia = new Set(esistenti.map((v) => chiave(v.fields.Title, v.fields.Soggetto)));
  const out: NuovaVoce[] = [];
  for (const m of scelte) {
    const base = { Title: m.fields.Title ?? "", Plico: m.fields.Plico ?? "Documentazione Amministrativa", Ordine: m.fields.Ordine };
    for (const soggetto of m.fields.PerComponente ? soggetti : [undefined]) {
      if (gia.has(chiave(base.Title, soggetto))) continue;
      out.push({ ...base, ...(soggetto ? { Soggetto: soggetto } : {}) });
    }
  }
  return out;
}

/** Crea le voci nella checklist della gara o di ciascun lotto indicato. Restituisce quante. */
export async function aggiungiVoci(r: GareRepos, g: Gara, lotti: Array<Lotto | undefined>, voci: (lotto?: Lotto) => NuovaVoce[]): Promise<number> {
  const items: ChecklistFields[] = lotti.flatMap((l) =>
    voci(l).map((v) => ({ ...v, Gara: g.id, Stato: "da fare" as const, ...(l ? { Lotto: l.id } : {}) })),
  );
  if (!items.length) return 0;
  return r.checklist.createMany(items);
}

/* ------------------------------- Modifica ------------------------------- */

export type DatiVoce = Pick<ChecklistFields, "Title" | "Plico" | "Soggetto" | "Stato" | "Note" | "FileNome" | "FileId" | "FileUrl">;

/** Un documento aggiunto a mano, in fondo al suo plico. */
export async function creaVoce(r: GareRepos, g: Gara, lotto: Lotto | undefined, dati: DatiVoce, voci: VoceChecklist[]): Promise<void> {
  if (!dati.Title?.trim()) throw new Error("Il nome del documento è obbligatorio.");
  if (dati.Note && dati.Note.length > 255) throw new Error("Le note possono avere al massimo 255 caratteri.");
  controllaLunghezze([["Documento", dati.Title.trim()], ["Soggetto", dati.Soggetto?.trim()], ["File", dati.FileNome?.trim()]]);
  const plico = dati.Plico ?? "Documentazione Amministrativa";
  const campi: ChecklistFields = { Gara: g.id, Plico: plico, Stato: dati.Stato ?? "da fare", Ordine: ordineInCoda(voci, plico), Title: dati.Title.trim() };
  for (const k of ["Soggetto", "Note", "FileNome", "FileId", "FileUrl"] as const) {
    const val = dati[k]?.trim();
    if (val) campi[k] = val;
  }
  if (lotto) campi.Lotto = lotto.id;
  await r.checklist.create(campi);
}

export async function salvaVoce(r: GareRepos, v: VoceChecklist, dati: Partial<DatiVoce>): Promise<void> {
  if (dati.Title !== undefined && !dati.Title.trim()) throw new Error("Il nome del documento è obbligatorio.");
  if (dati.Note && dati.Note.length > 255) throw new Error("Le note possono avere al massimo 255 caratteri.");
  controllaLunghezze([["Documento", dati.Title?.trim()], ["Soggetto", dati.Soggetto?.trim()], ["File", dati.FileNome?.trim()]]);
  const patch: FieldsPatch<ChecklistFields> = {};
  for (const [k, val] of Object.entries(dati)) (patch as Record<string, unknown>)[k] = val === undefined || val === "" ? null : typeof val === "string" ? val.trim() : val;
  await r.checklist.update(v.id, patch, v.etag);
}

export async function eliminaVoce(r: GareRepos, v: VoceChecklist): Promise<void> {
  await r.checklist.delete(v.id, v.etag);
}

/** Toglie le voci della gara (o di un lotto): usata quando si elimina la gara o il lotto. */
export async function eliminaChecklist(r: GareRepos, garaId: number, lotto?: Lotto): Promise<void> {
  const voci = await loadChecklist(r, garaId);
  for (const v of lotto ? voci.filter((x) => x.fields.Lotto === lotto.id) : voci) await r.checklist.delete(v.id);
}

/* ------------------------------- Modello ------------------------------- */

export type DatiModello = Pick<ModelloChecklistFields, "Title" | "Plico" | "Ordine" | "PerComponente" | "SoloRTI" | "Predefinito" | "Attivo">;

export async function salvaVoceModello(r: GareRepos, dati: DatiModello, esistente?: VoceModello): Promise<void> {
  if (!dati.Title?.trim()) throw new Error("Il nome del documento è obbligatorio.");
  controllaLunghezze([["Documento", dati.Title.trim()]]);
  const campi = {
    Title: dati.Title.trim(),
    Plico: dati.Plico ?? "Documentazione Amministrativa",
    Ordine: dati.Ordine,
    PerComponente: !!dati.PerComponente,
    SoloRTI: !!dati.SoloRTI,
    Predefinito: dati.Predefinito !== false,
    Attivo: dati.Attivo !== false,
  };
  if (esistente) await r.modelloChecklist.update(esistente.id, { ...campi, Ordine: campi.Ordine ?? null }, esistente.etag);
  else await r.modelloChecklist.create(campi);
}

export async function eliminaVoceModello(r: GareRepos, m: VoceModello): Promise<void> {
  await r.modelloChecklist.delete(m.id, m.etag);
}

/** Ordine per una nuova voce in fondo al suo plico. */
export function ordineInCoda(voci: Array<{ fields: { Plico?: Plico; Ordine?: number } }>, plico: Plico): number {
  const max = Math.max(0, ...voci.filter((v) => v.fields.Plico === plico).map((v) => v.fields.Ordine ?? 0));
  return Math.ceil((max + 1) / 10) * 10;
}
